import React from 'react';
import { useTranslation } from 'react-i18next';
import { up } from 'styled-breakpoints';
import styled from 'styled-components';
import { useApiKeySettings } from 'src/components/domain/users/components/profile-settings-modal/hooks/use-api-key-settings';
import { BaseFormProps } from 'src/components/domain/users/components/profile-settings-modal/profile-settings-modal';
import { ButtonV2 } from 'src/components/general/button/lgg-button';
import { useShowNotification } from 'src/components/general/feedback/hooks/use-show-notification';
import { TextInput } from 'src/components/general/inputs/text-input';
import { LoadingIndicator } from 'src/components/loading-fallback';
import { publicApiUrl } from 'src/services/http/helpers';
import { copyTextToClipboard } from 'src/utils/copy-to-clipboard-helpers';

export const API_DOCUMENTATION_URL = `${publicApiUrl()}/docs`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 17px;
  padding: 10px 0 0 0;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SectionTitle = styled.span`
  margin: 0;
  padding: 0;
  border: 0;
  color: ${({ theme }) => theme.colors.smalt};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 14px;
  line-height: 22px;
`;

const ActionRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const GenerateButton = styled(ButtonV2)`
  height: auto;
  padding: 8px 15px;
  gap: 10px;
  line-height: 18px;

  & > span {
    width: auto;
  }

  & [data-element='leading-icon']:not(:only-child) {
    margin-right: 0;
  }

  ${up('md')} {
    padding: 6px 15px;
  }
`;

const CompactButton = styled(ButtonV2)`
  display: flex;
  height: auto;
  padding: 8px 15px;
  align-items: center;
  gap: 10px;

  & > span {
    width: auto;
  }

  & [data-element='leading-icon']:not(:only-child) {
    margin-right: 0;
  }

  ${up('md')} {
    padding: 6px 15px;
  }
`;

const DocLink = styled.a`
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 12px;
  line-height: 15px;
  color: ${({ theme }) => theme.colors.gogo};
  text-decoration: none;
  align-self: flex-start;

  &:hover {
    text-decoration: underline;
  }
`;

type ApiKeySectionProps = BaseFormProps;

export const ApiKeySection: React.FC<ApiKeySectionProps> = ({ formId }) => {
  const {
    queryLoading,
    mutationLoading,
    hasExistingKey,
    maskedToken,
    plaintextToken,
    handleRegenerate,
    handleReset,
  } = useApiKeySettings();

  const { t } = useTranslation(['user']);
  const showNotification = useShowNotification();

  if (queryLoading) {
    return <LoadingIndicator />;
  }

  const canCopy = plaintextToken !== null;
  const showInput = hasExistingKey;
  const label = t('user:profileSettings.sections.apiKey.tokenLabel');

  const handleCopy = () => {
    if (plaintextToken) {
      copyTextToClipboard(plaintextToken);
      showNotification({
        type: 'success',
        title: t('user:profileSettings.sections.apiKey.toasts.copySuccess'),
      });
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
  };

  return (
    <StyledForm
      data-lgg-id="api-key-form"
      id={formId}
      onSubmit={handleSubmit}
      onReset={handleReset}
    >
      {showInput ? (
        <FieldGroup>
          <TextInput
            data-lgg-id="api-key-token-input"
            label={label}
            value={maskedToken ?? ''}
            readOnly
            reserveErrorArea={false}
          />
          <ActionRow>
            <CompactButton
              size="small"
              variant="default"
              icon="copy"
              data-lgg-id="copy-token-button"
              type="button"
              disabled={!canCopy}
              onClick={handleCopy}
            >
              {t('user:profileSettings.sections.apiKey.buttons.copyToken')}
            </CompactButton>
            <CompactButton
              size="small"
              variant="default"
              icon="update"
              data-lgg-id="regenerate-button"
              type="button"
              disabled={mutationLoading}
              onClick={() => void handleRegenerate()}
            >
              {t('user:profileSettings.sections.apiKey.buttons.regenerate')}
            </CompactButton>
          </ActionRow>
        </FieldGroup>
      ) : (
        <FieldGroup>
          <SectionTitle>{label}</SectionTitle>
          <ActionRow>
            <GenerateButton
              size="small"
              variant="primary"
              icon="key"
              data-lgg-id="generate-token-button"
              type="button"
              disabled={mutationLoading}
              onClick={() => void handleRegenerate()}
            >
              {t('user:profileSettings.sections.apiKey.buttons.generate')}
            </GenerateButton>
          </ActionRow>
        </FieldGroup>
      )}

      <DocLink
        href={API_DOCUMENTATION_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-lgg-id="api-documentation-link"
      >
        {t('user:profileSettings.sections.apiKey.documentationLink')}
      </DocLink>
    </StyledForm>
  );
};
